import React, { useState, useEffect } from 'react';
import {
    User, FileText, BookOpen, Tag, History, GitBranch,
    FileMinus2, Download, ArrowRightLeft, CheckCircle, XCircle
} from 'lucide-react';
import { submissionService, Submission, SubmissionVersion, SubmissionStatus } from '../services/adminAuth';
import { FILE_BASE_URL } from '../../../api/axios';
import { downloadFileFromUrl, getExtensionFromUrl } from '../../../utils/downloadHelper';

interface AdminSubmissionDetailsProps {
    submissionId: string;
}

const AdminSubmissionDetails: React.FC<AdminSubmissionDetailsProps> = ({ submissionId }) => {
    const [submission, setSubmission] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            try {
                const res = await submissionService.getSubmissionById(submissionId);
                if (res.isSuccess && res.data) setSubmission(res.data);
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, [submissionId]);

    if (loading) return (
        <div className="flex justify-center items-center py-20">
            <div className="w-6 h-6 border-2 border-gray-200 border-t-[#088A61] rounded-full animate-spin" />
        </div>
    );

    if (!submission) return (
        <p className="text-gray-500 text-center py-10">Unable to load submission details.</p>
    );

    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
    });

    const formatDateTime = (dateString: string) => {
        const d = new Date(dateString);
        return {
            date: d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' }),
            time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }),
        };
    };

    const getApplicationType = (type: number) => type === 1 ? 'Book' : 'Journal Article';

    const getStatusBadge = (status: number | string) => {
        const statusMap: Record<string, { label: string; className: string }> = {
            '1': { label: 'Submitted', className: 'bg-gray-100 text-gray-700' },
            '2': { label: 'Under Review', className: 'bg-blue-50 text-blue-700' },
            '3': { label: 'Returned', className: 'bg-yellow-50 text-yellow-700' },
            '4': { label: 'Rejected', className: 'bg-red-50 text-red-700' },
            '5': { label: 'Approved', className: 'bg-green-50 text-green-700' },
            'submitted': { label: 'Submitted', className: 'bg-gray-100 text-gray-700' },
            'underreview': { label: 'Under Review', className: 'bg-blue-50 text-blue-700' },
            'returned': { label: 'Returned', className: 'bg-yellow-50 text-yellow-700' },
            'rejected': { label: 'Rejected', className: 'bg-red-50 text-red-700' },
            'approved': { label: 'Approved', className: 'bg-green-50 text-green-700' },
        };
        const key = String(status).toLowerCase().replace(/\s/g, '');
        const config = statusMap[key] ?? { label: String(status), className: 'bg-gray-100 text-gray-700' };
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-md text-xs font-medium ${config.className}`}>
                {config.label}
            </span>
        );
    };

    const stageMap: Record<number, string> = {
        1: 'ES Registry', 2: 'ES', 3: 'DESS', 4: 'TAG Divisional Head',
        5: 'TAG Chairman', 6: 'Book Head', 7: 'Journal Head',
        8: 'Thematic Area Leader', 9: 'Thematic Area Members',
        10: 'Reviewer', 11: 'ESS Staff', 12: 'Author'
    };

    const sub = submission;
    const submissionVersions = sub.versions as SubmissionVersion[] | undefined;
    const rawHistories = sub.workflowHistories as Array<any> | undefined;
    const workflowHistories = rawHistories
        ? [...rawHistories].sort((a, b) => new Date(b.actionAt).getTime() - new Date(a.actionAt).getTime())
        : [];

    const latestManuscriptUrl = submissionVersions && submissionVersions.length > 0
        ? submissionVersions[submissionVersions.length - 1].manuscriptUrl
        : sub.manuscriptUrl;

    const bookAuthors = sub.bookAuthors as Array<{
        id: string; name: string; isLeadAuthor: boolean; affiliateInstitution: string;
    }> | undefined;

    return (
        <div className="space-y-0 divide-y divide-gray-100">

            {/* ── Header ── */}
            <div className="pb-5 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700">
                        {getApplicationType(sub.applicationType)}
                    </span>
                    {getStatusBadge(sub.status)}
                    {/* Assessment Status - Dynamic based on application type */}
                    {sub.applicationType === 1 ? (
                        // Book: Prima Facie
                        sub.completedPrimaFacie ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700">
                                <CheckCircle className="w-3 h-3" /> Prima Facie Complete
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600">
                                <XCircle className="w-3 h-3" /> Prima Facie Pending
                            </span>
                        )
                    ) : (
                        // Journal: Journal Assessment
                        sub.completedPrimaFacie ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700">
                                <CheckCircle className="w-3 h-3" /> Journal Assessment Complete
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-red-50 text-red-600">
                                <XCircle className="w-3 h-3" /> Journal Assessment Pending
                            </span>
                        )
                    )}
                </div>
                <div>
                    <h2 className="text-xl font-bold text-gray-900 leading-snug">{sub.title}</h2>
                    <p className="text-xs text-gray-400 mt-1 font-mono">{sub.submissionNumber}</p>
                </div>
            </div>

            {/* ── Quick stats ── */}
            <div className="py-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Submitted</p>
                    <p className="text-sm font-medium text-gray-800">{formatDate(sub.submittedAt)}</p>
                </div>
                <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Thematic Area</p>
                    <p className="text-sm font-medium text-gray-800">{sub.categoryName || sub.category?.name || 'N/A'}</p>
                </div>
                <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Current Desk</p>
                    <p className="text-sm font-bold text-[#088A61]">
                        {stageMap[sub.currentStage] || `Stage ${sub.currentStage}` || 'N/A'}
                    </p>
                </div>
                <div>
                    <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Similarity</p>
                    <p className={`text-sm font-bold ${sub.plagiarismOutcome >= 25 ? 'text-red-600' : sub.plagiarismOutcome >= 15 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {sub.plagiarismOutcome}%
                    </p>
                </div>
            </div>

            {/* ── Author ── */}
            <div className="py-5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" /> Author
                </h3>
                {sub.author?.person ? (
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center text-green-700 font-bold text-sm flex-shrink-0">
                            {sub.author.person.firstName?.[0]}{sub.author.person.lastName?.[0]}
                        </div>
                        <div className="min-w-0">
                            <p className="font-semibold text-gray-900">{sub.author.person.fullName}</p>
                            <p className="text-sm text-gray-500">{sub.author.person.email}</p>
                            {sub.author.person.phoneNo && (
                                <p className="text-xs text-gray-400 mt-0.5">{sub.author.person.phoneNo}</p>
                            )}
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 italic">Author information unavailable</p>
                )}

                {/* Book authors / Editorial board members list */}
                {bookAuthors && bookAuthors.length > 0 && (
                    <div className="mt-4 space-y-2">
                        <p className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                            {sub.applicationType === 2 ? `Editorial Board Members (${bookAuthors.length})` : 'All Book Authors'}
                        </p>
                        {bookAuthors.map((a) => (
                            <div key={a.id} className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-blue-50 flex items-center justify-center text-blue-700 font-bold text-xs flex-shrink-0">
                                    {a.name.charAt(0)}
                                </div>
                                <div>
                                    <span className="text-sm text-gray-700">{a.name}</span>
                                    {sub.applicationType !== 2 && a.isLeadAuthor && (
                                        <span className="ml-1.5 text-[10px] font-semibold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">Lead</span>
                                    )}
                                    {a.affiliateInstitution && (
                                        <p className="text-xs text-gray-400">{a.affiliateInstitution}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Flat authors string fallback (book flow only — for journals the
                    string is the Journal Leader and the array carries the board) */}
                {sub.authors && sub.applicationType !== 2 && (!bookAuthors || bookAuthors.length === 0) && (
                    <div className="mt-3 pt-3 border-t border-gray-50">
                        <p className="text-xs text-gray-400 mb-1">All listed authors</p>
                        <p className="text-sm text-gray-700">{sub.authors}</p>
                    </div>
                )}
            </div>

            {/* ── Journal Details (journals only) ── */}
            {sub.applicationType === 2 && (
                <div className="py-5 space-y-3">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <BookOpen className="w-3.5 h-3.5" /> Journal Details
                    </h3>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                        {sub.authors && (
                            <div className="col-span-2">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Name of Journal Leader</p>
                                <p className="text-sm text-gray-800">{sub.authors}</p>
                            </div>
                        )}
                        {sub.positionOfJournalRepresentative && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Position of Journal Representative</p>
                                <p className="text-sm text-gray-800">{sub.positionOfJournalRepresentative}</p>
                            </div>
                        )}
                        {sub.journalProfessionalAssociation && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Journal Professional Association</p>
                                <p className="text-sm text-gray-800">{sub.journalProfessionalAssociation}</p>
                            </div>
                        )}
                        {sub.isbnNo && sub.isbnNo !== 'N/A' && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">ISSN</p>
                                <p className="text-sm font-mono text-gray-800">{sub.isbnNo}</p>
                            </div>
                        )}
                        {sub.volume && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Current Volume</p>
                                <p className="text-sm text-gray-800">{sub.volume}</p>
                            </div>
                        )}
                        {sub.issue && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Current Issue</p>
                                <p className="text-sm text-gray-800">{sub.issue}</p>
                            </div>
                        )}
                        {sub.dateOfJournalCommencement && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Date of Journal Commencement</p>
                                <p className="text-sm text-gray-800">{formatDate(sub.dateOfJournalCommencement)}</p>
                            </div>
                        )}
                        {sub.journalBodyArticleAccessibility && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Article Accessibility</p>
                                <p className="text-sm text-gray-800">{sub.journalBodyArticleAccessibility}</p>
                            </div>
                        )}
                        {sub.journalBodyPhysicalAddress && (
                            <div className="col-span-2">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Physical Address</p>
                                <p className="text-sm text-gray-800">{sub.journalBodyPhysicalAddress}</p>
                            </div>
                        )}
                        {sub.publicationUrl && (
                            <div className="col-span-2">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Journal URL</p>
                                <a href={sub.publicationUrl} target="_blank" rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline break-all">
                                    {sub.publicationUrl}
                                </a>
                            </div>
                        )}
                        {sub.journalBodyWebsite && (
                            <div className="col-span-2">
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-0.5">Association Body Website</p>
                                <a href={sub.journalBodyWebsite} target="_blank" rel="noopener noreferrer"
                                    className="text-sm text-blue-600 hover:underline break-all">
                                    {sub.journalBodyWebsite}
                                </a>
                            </div>
                        )}
                    </div>
                    {sub.keywords && (
                        <div>
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-1 flex items-center gap-1">
                                <Tag className="w-3 h-3" /> Keywords
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {sub.keywords.split(',').map((kw: string, i: number) => (
                                    <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{kw.trim()}</span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Supporting documents for journals */}
                    {(sub.journalBodyOperationalGuideLineFileUrl || sub.journalBodyPolicyFileUrl) && (
                        <div className="pt-3 border-t border-gray-100 space-y-2">
                            <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">Supporting Documents</p>
                            {sub.journalBodyOperationalGuideLineFileUrl && (
                                <a
                                    href={sub.journalBodyOperationalGuideLineFileUrl.startsWith('http')
                                        ? sub.journalBodyOperationalGuideLineFileUrl
                                        : `${FILE_BASE_URL}/${sub.journalBodyOperationalGuideLineFileUrl.replace(/^\//, '')}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                >
                                    <FileText className="w-3.5 h-3.5" /> Publication & Operational Guidelines
                                </a>
                            )}
                            {sub.journalBodyPolicyFileUrl && (
                                <a
                                    href={sub.journalBodyPolicyFileUrl.startsWith('http')
                                        ? sub.journalBodyPolicyFileUrl
                                        : `${FILE_BASE_URL}/${sub.journalBodyPolicyFileUrl.replace(/^\//, '')}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline"
                                >
                                    <FileText className="w-3.5 h-3.5" /> Journal Policy
                                </a>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* ── Abstract / Preface ── */}
            <div className="py-5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" /> {sub.applicationType === 1 ? 'Preface' : 'Abstract'}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed text-justify whitespace-pre-wrap">{sub.abstract}</p>
            </div>

            {/* ── Prima Facie Decision ── */}
            {sub.primaFacieFinalDecision && (
                <div className="py-5">
                   <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                        <ArrowRightLeft className="w-3.5 h-3.5" /> 
                        {sub.applicationType === 1 ? 'Prima Facie Decision' : 'Journal Assessment Decision'}
                    </h3>
                    <div className="bg-gray-50 rounded-xl border border-gray-100 p-4 space-y-3">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                                sub.primaFacieFinalDecision.decision === 'Recommended'
                                    ? 'bg-green-50 text-green-700 border border-green-100'
                                    : 'bg-red-50 text-red-700 border border-red-100'
                            }`}>
                                {sub.primaFacieFinalDecision.decision || 'Pending'}
                            </span>
                            
                            {sub.primaFacieFinalDecision.decision !== 'Recommended' && (
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                    sub.primaFacieFinalDecision.allowResubmission
                                        ? 'bg-green-50 text-green-700'
                                        : 'bg-red-50 text-red-500'
                                }`}>
                                    Resubmission: {sub.primaFacieFinalDecision.allowResubmission ? 'Allowed' : 'Not Allowed'}
                                </span>
                            )}
                        </div>
                        {sub.primaFacieFinalDecision.comment && (
                            <div>
                                <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400 mb-1">Comment</p>
                                <p className="text-sm text-gray-700 italic">"{sub.primaFacieFinalDecision.comment}"</p>
                            </div>
                        )}
                        {sub.primaFacieFinalDecision.dateCreated && (
                            <p className="text-xs text-gray-400">
                                Decided on {formatDate(sub.primaFacieFinalDecision.dateCreated)}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* ── Workflow Trail ── */}
            <div className="py-5">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-1.5">
                    <History className="w-3.5 h-3.5" /> Submission Trail
                </h3>
                {workflowHistories.length > 0 ? (
                    <div className="relative">
                        <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />
                        <div className="space-y-4">
                            {workflowHistories.map((h, idx) => (
                                <div key={h.id} className="relative pl-9">
                                    <div className={`absolute left-0 w-6 h-6 rounded-full flex items-center justify-center border-2 ${idx === 0 ? 'bg-green-700 border-green-700' : 'bg-white border-gray-300'}`}>
                                        <GitBranch className={`w-3 h-3 ${idx === 0 ? 'text-white' : 'text-gray-400'}`} />
                                    </div>
                                    <div className={`rounded-lg p-3 border ${idx === 0 ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'}`}>
                                        <div className="flex items-start justify-between gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                                                    h.actionType === 'Forward' ? 'bg-green-100 text-green-700' :
                                                    h.actionType === 'Return' ? 'bg-yellow-100 text-yellow-700' :
                                                    'bg-gray-100 text-gray-600'
                                                }`}>{h.actionType}</span>
                                                <span className="text-xs text-gray-500">{h.fromStage} → {h.toStage}</span>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-500 whitespace-nowrap">{formatDateTime(h.actionAt).date}</p>
                                                <p className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(h.actionAt).time}</p>
                                            </div>
                                        </div>
                                        {h.comment && (
                                            <p className="text-xs text-gray-600 mt-1.5 italic">"{h.comment}"</p>
                                        )}
                                        {h.actionByUser?.person && (
                                            <p className="text-xs text-gray-400 mt-1">by {h.actionByUser.person.fullName}</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 italic">No workflow history available.</p>
                )}

                {/* Versions */}
                {submissionVersions && submissionVersions.length > 0 && (
                    <div className="mt-5">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                            <FileMinus2 className="w-3.5 h-3.5" /> Manuscript Versions
                        </p>
                        <div className="space-y-2">
                            {submissionVersions.map((v, idx) => (
                                <div key={v.id} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                    <div className="flex items-center gap-2">
                                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${idx === submissionVersions.length - 1 ? 'bg-green-700 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                            v{v.versionNumber}
                                        </span>
                                        <div>
                                            <p className="text-xs font-medium text-gray-700">Version {v.versionNumber}</p>
                                            <p className="text-xs text-gray-400">{formatDate(v.dateSubmitted)}</p>
                                        </div>
                                    </div>
                                    {idx === submissionVersions.length - 1 && (
                                        <span className="text-[10px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">Latest</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Download ── */}
            <div className="pt-5">
                <button
                    disabled={downloading || !latestManuscriptUrl}
                    onClick={async () => {
                        if (!latestManuscriptUrl) return;
                        setDownloading(true);
                        const url = latestManuscriptUrl.startsWith('http')
                            ? latestManuscriptUrl
                            : `${FILE_BASE_URL}/${latestManuscriptUrl}`;
                        const authorName = sub.author?.person?.fullName || 'Unknown_Author';
                        const appType = sub.applicationType === 1 ? 'Book' : 'Journal';
                        const extension = getExtensionFromUrl(latestManuscriptUrl);
                        const filename = `${authorName}_${sub.title || 'Untitled'}_${appType}_manuscript`
                            .replace(/[^a-z0-9]/gi, '_').toLowerCase() + extension;
                        try {
                            await downloadFileFromUrl(url, filename);
                        } finally {
                            setDownloading(false);
                        }
                    }}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-700 text-white rounded-lg font-medium hover:bg-green-800 transition-colors ${(downloading || !latestManuscriptUrl) ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                    <Download className={`w-5 h-5 ${downloading ? 'animate-pulse' : ''}`} />
                    {downloading ? 'Downloading...' : 'Download Manuscript'}
                </button>
            </div>
        </div>
    );
};

export default AdminSubmissionDetails;